import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";
import { sendReport, extractReportIdFromResponse } from "../../functions/sendReport";
import { ensureWelcomeStageRowId } from "../../functions/ensureWelcomeStageRowId";
import { isMultiplaySessionEnabled } from "../../functions/isMultiplaySession";
import { resolvePlayerName } from "../../functions/resolvePlayerName";
import { getDbUserId, getExternalUserId } from "../../functions/multiplayUserId";
import { WANDS } from "../wand/wandsConfig";
import { DARK_TARGETS, FRIENDLY_TARGETS } from "./spellcastingTargets";
import "../fonts/breuer-headline.css";
import "./game.css";
import timeIcon from "./img/time.png";
import pointIcon from "./img/point.png";
import game1DesktopBg from "../images/desktop/game1.png";
import game1MobileBg from "../images/mob_bg/game1_mob.png";
import game2DesktopBg from "../images/desktop/game2.png";
import game2MobileBg from "../images/mob_bg/game2_mob.png";
import Swal from "sweetalert2";

const HOGWARTS_SELECTION_KEY_BASE = "hogwartsSelectionData";

function getSelectionStorageKey(userLike) {
  const uid = getDbUserId(userLike);
  const sid = userLike?.sessionId;
  const oid = userLike?.organizationId;
  const scope = [uid, sid, oid]
    .map((v) => String(v ?? "").trim())
    .join(":");
  return scope.replace(/:/g, "") ? `${HOGWARTS_SELECTION_KEY_BASE}:${scope}` : HOGWARTS_SELECTION_KEY_BASE;
}

function readHogwartsSelectionArray(userLike) {
  try {
    const raw = localStorage.getItem(getSelectionStorageKey(userLike));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}


const DARK_HIT = 10;
const FRIENDLY_HIT = -5;

const TRIAL_RULES_ITEMS = [
  "Tap anywhere on the screen to cast spells",
  "Hit dark targets to earn points (+10 each)",
  "Avoid friendly targets, hitting them will reduce points (−5, score will not go below 0)",
  "Stay focused as the pace increases and complete all phases to finish the trial",
];

const POPUP_DURATION_MS = {
  combo: 2200,
  immersive: 3800,
  penalty: 2600,
};

const PHASES = [
  {
    durationMs: 22_000,
    intro: "Focus your wand…\nThe trial of spells begins.",
    end: "You've begun to understand the flow of magic…\nMore challenges await.",
    spawnMin: 1050,
    spawnMax: 1850,
    spawnBurstMin: 1,
    spawnBurstMax: 2,
    speedMin: 62,
    speedMax: 122,
    friendlyChance: 0.30,
    vyAmp: 14,
  },
  {
    durationMs: 26_000,
    intro: "The magic grows stronger…\nNow only precision will guide you.",
    end: "You have completed the Spellcasting Trial…\nYour control has been proven.",
    spawnMin: 700,
    spawnMax: 1350,
    spawnBurstMin: 2,
    spawnBurstMax: 3,
    speedMin: 88,
    speedMax: 168,
    friendlyChance: 0.28,
    vyAmp: 28,
  },
];

const IMMERSIVE = {
  patronus:
    "EXPECTO PATRONUM! A silver stag bursts forth, illuminating the shadows…",
  hedwig: "A loyal owl swoops by… do not strike!",
  snitch: "A streak of gold darts across — blink and it's gone!",
  hogwartsEcho:
    "The halls remember magic… your spells become part of that story.",
};

const SPELL_SPAWN_ANCHOR_IDS = [
  "spell-spawn-1",
  "spell-spawn-2",
  "spell-spawn-3",
  "spell-spawn-4",
  "spell-spawn-5",
];

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function distSq(ax, ay, bx, by) {
  const dx = ax - bx;
  const dy = ay - by;
  return dx * dx + dy * dy;
}

// ─── NEW: Dynamic Lightning Bolt SVG ──────────────────────────────────────────
// Generates a jagged, branching lightning path like real wand magic.
// Each frame the filaments re-randomize slightly to create a flickering/alive feel.

function generateLightningPath(totalLen, amplitude, segments) {
  // Midpoint displacement along the bolt axis
  const points = [{ t: 0, d: 0 }];
  for (let i = 1; i < segments; i++) {
    const t = i / segments;
    const displacement = (Math.random() - 0.5) * amplitude * 2 * (1 - t * 0.5);
    points.push({ t, d: displacement });
  }
  points.push({ t: 1, d: 0 });

  let d = `M 0,0`;
  for (let i = 1; i < points.length; i++) {
    const x = points[i].t * totalLen;
    const y = points[i].d;
    d += ` L ${x.toFixed(1)},${y.toFixed(1)}`;
  }
  return { d, points };
}

function generateBranch(parentPoints, branchAt, branchLen, amplitude) {
  const idx = Math.floor(branchAt * parentPoints.length);
  const origin = parentPoints[Math.min(idx, parentPoints.length - 1)];
  const startX = origin.t * branchLen * 3;
  const startY = origin.d;
  const angle = (Math.random() - 0.5) * 1.2; // radians offset
  const segs = 5;
  let d = `M ${startX.toFixed(1)},${startY.toFixed(1)}`;
  let cx = startX;
  let cy = startY;
  for (let i = 1; i <= segs; i++) {
    const t = i / segs;
    cx += (branchLen / segs) * Math.cos(angle);
    cy += (branchLen / segs) * Math.sin(angle) + (Math.random() - 0.5) * amplitude * 0.8;
    d += ` L ${cx.toFixed(1)},${cy.toFixed(1)}`;
  }
  return d;
}

// Particle emitter state for sparks around the bolt
class SparkParticle {
  constructor(x, y) {
    this.x = x;
    this.y = y;
    const angle = Math.random() * Math.PI * 2;
    const speed = 30 + Math.random() * 80;
    this.vx = Math.cos(angle) * speed;
    this.vy = Math.sin(angle) * speed;
    this.life = 1.0;
    this.decay = 0.04 + Math.random() * 0.06;
    this.size = 1.5 + Math.random() * 3;
    this.color = Math.random() < 0.6 ? '#00ff88' : '#ffffff';
  }
  update(dt) {
    this.x += this.vx * dt;
    this.y += this.vy * dt;
    this.vy += 60 * dt; // gentle gravity
    this.life -= this.decay;
  }
  get alive() { return this.life > 0; }
}

// Canvas-based dynamic lightning renderer
function LightningCanvas({ bolt, stageW, stageH }) {
  const canvasRef = useRef(null);
  const frameRef = useRef(0);
  const sparksRef = useRef([]);
  const lastSparkRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const draw = (ts) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const { fromX, fromY, toX, toY } = bolt;
      const dx = toX - fromX;
      const dy = toY - fromY;
      const len = Math.hypot(dx, dy);
      if (len < 2) {
        frameRef.current = requestAnimationFrame(draw);
        return;
      }
      const angle = Math.atan2(dy, dx);

      ctx.save();
      ctx.translate(fromX, fromY);
      ctx.rotate(angle);

      // ── Main bolt: 3 layered passes for glow depth ──
      const amplitude = Math.max(8, len * 0.07);
      const segs = Math.max(10, Math.floor(len / 12));

      for (let pass = 0; pass < 3; pass++) {
        const { d, points } = generateLightningPath(len, amplitude, segs);
        const path = new Path2D(d);

        if (pass === 0) {
          // Outer glow — wide, dim
          ctx.strokeStyle = 'rgba(0,255,120,0.12)';
          ctx.lineWidth = 18;
          ctx.filter = 'blur(8px)';
          ctx.stroke(path);
        } else if (pass === 1) {
          // Mid glow — green
          ctx.strokeStyle = 'rgba(60,255,140,0.55)';
          ctx.lineWidth = 5;
          ctx.filter = 'blur(2px)';
          ctx.stroke(path);

          // Branches
          const numBranches = 2 + Math.floor(Math.random() * 3);
          for (let b = 0; b < numBranches; b++) {
            const branchPath = new Path2D(generateBranch(
              points, 0.2 + Math.random() * 0.6, len * (0.12 + Math.random() * 0.2), amplitude * 0.6
            ));
            ctx.strokeStyle = 'rgba(100,255,160,0.35)';
            ctx.lineWidth = 1.5;
            ctx.filter = 'blur(1px)';
            ctx.stroke(branchPath);
          }
        } else {
          // Core — bright white-green
          ctx.strokeStyle = 'rgba(220,255,235,0.95)';
          ctx.lineWidth = 1.8;
          ctx.filter = 'none';
          ctx.stroke(path);
        }
      }

      ctx.restore();

      // ── Origin burst at wand tip ──
      const tipGrad = ctx.createRadialGradient(fromX, fromY, 0, fromX, fromY, 22);
      tipGrad.addColorStop(0, 'rgba(255,255,255,0.95)');
      tipGrad.addColorStop(0.3, 'rgba(180,255,200,0.6)');
      tipGrad.addColorStop(0.7, 'rgba(0,255,100,0.2)');
      tipGrad.addColorStop(1, 'transparent');
      ctx.beginPath();
      ctx.arc(fromX, fromY, 22, 0, Math.PI * 2);
      ctx.fillStyle = tipGrad;
      ctx.fill();

      // ── Impact burst at target ──
      const impactGrad = ctx.createRadialGradient(toX, toY, 0, toX, toY, 30);
      impactGrad.addColorStop(0, 'rgba(255,255,255,0.8)');
      impactGrad.addColorStop(0.4, 'rgba(100,255,150,0.5)');
      impactGrad.addColorStop(1, 'transparent');
      ctx.beginPath();
      ctx.arc(toX, toY, 30, 0, Math.PI * 2);
      ctx.fillStyle = impactGrad;
      ctx.fill();

      // ── Sparks ──
      const dt = 1 / 60;
      if (ts - lastSparkRef.current > 40) {
        // Emit sparks along the bolt
        for (let i = 0; i < 3; i++) {
          const t = Math.random();
          const sx = fromX + dx * t;
          const sy = fromY + dy * t;
          sparksRef.current.push(new SparkParticle(sx, sy));
        }
        // Extra sparks at tip origin
        for (let i = 0; i < 2; i++) {
          sparksRef.current.push(new SparkParticle(fromX + (Math.random()-0.5)*4, fromY + (Math.random()-0.5)*4));
        }
        lastSparkRef.current = ts;
      }

      sparksRef.current = sparksRef.current.filter(s => s.alive);
      for (const s of sparksRef.current) {
        s.update(dt);
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.size * s.life, 0, Math.PI * 2);
        ctx.fillStyle = s.color.replace(')', `,${(s.life * 0.9).toFixed(2)})`).replace('rgb', 'rgba');
        // simple approach: use globalAlpha
        ctx.globalAlpha = s.life * 0.85;
        ctx.fillStyle = s.color;
        ctx.fill();
        ctx.globalAlpha = 1;
      }

      frameRef.current = requestAnimationFrame(draw);
    };

    frameRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frameRef.current);
  }, [bolt]);

  return (
    <canvas
      ref={canvasRef}
      className="spell-lightning-canvas"
      width={stageW}
      height={stageH}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 9,
        pointerEvents: 'none',
        width: '100%',
        height: '100%',
      }}
    />
  );
}

// Per-bolt canvas (one canvas per active bolt, sized to cover the bolt area with padding)
function BoltCanvas({ bolt, stageW, stageH }) {
  const canvasRef = useRef(null);
  const frameRef = useRef(0);
  const sparksRef = useRef([]);
  const tickRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const animate = (ts) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const { fromX, fromY, toX, toY } = bolt;
      const dx = toX - fromX;
      const dy = toY - fromY;
      const len = Math.hypot(dx, dy);
      if (len < 2) {
        frameRef.current = requestAnimationFrame(animate);
        return;
      }
      const angle = Math.atan2(dy, dx);
      const amplitude = Math.max(10, len * 0.085);
      const segs = Math.max(12, Math.floor(len / 10));

      // ── Pass 1: wide outer halo ──
      ctx.save();
      ctx.translate(fromX, fromY);
      ctx.rotate(angle);
      const { d: d1 } = generateLightningPath(len, amplitude * 1.1, segs);
      const path1 = new Path2D(d1);
      ctx.strokeStyle = 'rgba(0,200,100,0.08)';
      ctx.lineWidth = 28;
      ctx.filter = 'blur(12px)';
      ctx.stroke(path1);
      ctx.restore();

      // ── Pass 2: main glow filaments ──
      for (let f = 0; f < 4; f++) {
        ctx.save();
        ctx.translate(fromX, fromY);
        ctx.rotate(angle);
        const { d, points: pts } = generateLightningPath(len, amplitude * (0.7 + f * 0.15), segs);
        const path = new Path2D(d);

        if (f === 0) {
          ctx.strokeStyle = 'rgba(0,255,100,0.22)';
          ctx.lineWidth = 10;
          ctx.filter = 'blur(5px)';
        } else if (f === 1) {
          ctx.strokeStyle = 'rgba(80,255,150,0.45)';
          ctx.lineWidth = 4;
          ctx.filter = 'blur(2px)';
        } else if (f === 2) {
          ctx.strokeStyle = 'rgba(160,255,190,0.7)';
          ctx.lineWidth = 2;
          ctx.filter = 'blur(0.8px)';
        } else {
          // core bright filament
          ctx.strokeStyle = 'rgba(240,255,245,0.95)';
          ctx.lineWidth = 1.2;
          ctx.filter = 'none';
        }
        ctx.stroke(path);

        // ── Branches on filament 1 ──
        if (f === 1) {
          const numB = 2 + Math.floor(Math.random() * 3);
          for (let b = 0; b < numB; b++) {
            const bPath = new Path2D(generateBranch(pts, 0.15 + Math.random() * 0.65, len * (0.1 + Math.random() * 0.25), amplitude * 0.5));
            ctx.strokeStyle = 'rgba(100,255,160,0.3)';
            ctx.lineWidth = 1.2;
            ctx.filter = 'blur(1px)';
            ctx.stroke(bPath);
          }
        }
        ctx.restore();
      }

      // ── Wand tip burst ──
      ctx.save();
      // Pulsing ring
      const pulseScale = 1 + 0.3 * Math.sin(ts * 0.015);
      const tipR = 18 * pulseScale;
      const tipGrad = ctx.createRadialGradient(fromX, fromY, 0, fromX, fromY, tipR * 2.2);
      tipGrad.addColorStop(0, 'rgba(255,255,255,1)');
      tipGrad.addColorStop(0.25, 'rgba(200,255,220,0.85)');
      tipGrad.addColorStop(0.55, 'rgba(0,255,120,0.4)');
      tipGrad.addColorStop(1, 'transparent');
      ctx.beginPath();
      ctx.arc(fromX, fromY, tipR * 2.2, 0, Math.PI * 2);
      ctx.fillStyle = tipGrad;
      ctx.fill();

      // ── Impact burst ──
      const impactR = 24 + 6 * Math.sin(ts * 0.018 + 1);
      const impGrad = ctx.createRadialGradient(toX, toY, 0, toX, toY, impactR);
      impGrad.addColorStop(0, 'rgba(255,255,255,0.9)');
      impGrad.addColorStop(0.35, 'rgba(120,255,170,0.6)');
      impGrad.addColorStop(1, 'transparent');
      ctx.beginPath();
      ctx.arc(toX, toY, impactR, 0, Math.PI * 2);
      ctx.fillStyle = impGrad;
      ctx.fill();
      ctx.restore();

      // ── Sparks ──
      tickRef.current++;
      if (tickRef.current % 2 === 0) {
        // Sparks along the bolt
        for (let i = 0; i < 4; i++) {
          const t2 = Math.random();
          sparksRef.current.push(new SparkParticle(
            fromX + dx * t2 + (Math.random() - 0.5) * 6,
            fromY + dy * t2 + (Math.random() - 0.5) * 6
          ));
        }
        // Sparks at wand tip origin
        for (let i = 0; i < 3; i++) {
          sparksRef.current.push(new SparkParticle(
            fromX + (Math.random() - 0.5) * 10,
            fromY + (Math.random() - 0.5) * 10
          ));
        }
      }

      const dt = 1 / 60;
      sparksRef.current = sparksRef.current.filter(s => s.alive);
      for (const s of sparksRef.current) {
        s.update(dt);
        ctx.save();
        ctx.globalAlpha = Math.max(0, s.life * 0.9);
        // elongated spark line
        ctx.beginPath();
        ctx.arc(s.x, s.y, Math.max(0.5, s.size * s.life), 0, Math.PI * 2);
        ctx.fillStyle = s.color;
        ctx.fill();
        ctx.restore();
      }

      frameRef.current = requestAnimationFrame(animate);
    };

    frameRef.current = requestAnimationFrame(animate);
    return () => {
      cancelAnimationFrame(frameRef.current);
    };
  }, [bolt, stageW, stageH]);

  return (
    <canvas
      ref={canvasRef}
      width={stageW || 800}
      height={stageH || 600}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 9,
        pointerEvents: 'none',
        width: '100%',
        height: '100%',
      }}
    />
  );
}

function Welcome() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const adminToken = useSelector(selectAdminToken);

  const { wandId, houseId, characterId, characterName, wandName } = location.state ?? {};
  const wandDef = WANDS.find((w) => w.id === wandId) ?? WANDS[0];
  const handSrc = wandDef.handImage ?? wandDef.image;

  const [elapsedTime, setElapsedTime] = useState(0);
  const [gameComplete, setGameComplete] = useState(false);
  const [score, setScore] = useState(0);
  const [phaseIndex, setPhaseIndex] = useState(0);
  const [phaseUi, setPhaseUi] = useState("intro");
  const [phaseUiText, setPhaseUiText] = useState(PHASES[0].intro);
  const [streak, setStreak] = useState(0);
  const [popupSlot, setPopupSlot] = useState(null);
  const [penaltyFlash, setPenaltyFlash] = useState(false);
  const [hitFlash, setHitFlash] = useState(false);
  const [spellLevelDone, setSpellLevelDone] = useState(false);
  const [targetsSnap, setTargetsSnap] = useState([]);
  // boltsSnap now stores enriched bolt data: { id, fromX, fromY, toX, toY }
  const [boltsSnap, setBoltsSnap] = useState([]);
  const [wandAnchorRatio, setWandAnchorRatio] = useState(0.5);
  const [stageSize, setStageSize] = useState({ w: 800, h: 600 });

  const headerSet = useRef(false);
  const wrongCountRef = useRef(0);
  const totalImagesCountRef = useRef(0);
  const scoreRef = useRef(0);
  const elapsedTimeRef = useRef(0);
  const finishOnceRef = useRef(false);
  const completeGameRef = useRef(() => { });

  const reportIdRef = useRef(null);
  const stageRowIdRef = useRef(null);
  const demoTargetsHitRef = useRef(0);
  const demoRedirectTriggeredRef = useRef(false);

  const [crashingTargets, setCrashingTargets] = useState([]);
  const [fragments, setFragments] = useState([]);
  const [glassBreaks, setGlassBreaks] = useState([]);
  const [rulesModalOpen, setRulesModalOpen] = useState(true);

  const storedUserData = JSON.parse(
    localStorage.getItem("userData") || sessionStorage.getItem("userData") || "{}"
  ) || {};

  const sid = String(storedUserData?.sessionId ?? "").toLowerCase();
  const oid = String(storedUserData?.organizationId ?? "").toLowerCase();
  const reportRoleForApi =
    storedUserData.user_type ||
    storedUserData.userType ||
    storedUserData.role ||
    (storedUserData.source === "DEMO" ? "demobypass" : null) ||
    storedUserData.source ||
    "ORG_USER";
  const isDemoBypass =
    String(reportRoleForApi).toLowerCase() === "demobypass" ||
    storedUserData?.source === "DEMO" ||
    (sid === "demobypass" && oid === "demobypass");

  const stageRef = useRef(null);
  const wandRef = useRef(null);
  const targetsRef = useRef([]);
  const projectilesRef = useRef([]);
  const idRef = useRef(1);
  const rafRef = useRef(0);
  const lastTsRef = useRef(0);
  const phaseStartRef = useRef(0);
  const nextSpawnAtRef = useRef(0);
  const streakRef = useRef(0);
  const popupQueueRef = useRef([]);
  const phaseUiRef = useRef("intro");
  const phaseIndexRef = useRef(0);
  const spellLevelDoneRef = useRef(false);
  const introTimerRef = useRef(0);
  const phaseEndingRef = useRef(false);
  const wandMoveRafRef = useRef(0);
  const wandRatioPendingRef = useRef(0.5);

  // Track wand tip position for bolt origin
  const wandTipRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    phaseUiRef.current = phaseUi;
  }, [phaseUi]);

  useEffect(() => {
    phaseIndexRef.current = phaseIndex;
  }, [phaseIndex]);

  useEffect(() => {
    spellLevelDoneRef.current = spellLevelDone;
  }, [spellLevelDone]);

  useEffect(
    () => () => {
      if (wandMoveRafRef.current) {
        cancelAnimationFrame(wandMoveRafRef.current);
        wandMoveRafRef.current = 0;
      }
    },
    []
  );

  // Track stage size for canvas dimensions
  useEffect(() => {
    const updateSize = () => {
      if (stageRef.current) {
        const r = stageRef.current.getBoundingClientRect();
        setStageSize({ w: Math.round(r.width), h: Math.round(r.height) });
      }
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  useEffect(() => {
    if (headerSet.current) return;
    dispatch(setBackButtonUrl("/stage1_intro"));
    headerSet.current = true;
  }, [dispatch]);

  useEffect(() => {
    const body = document.body;
    const prevBg = body.style.backgroundImage;
    const prevBgSize = body.style.backgroundSize;
    const prevBgPos = body.style.backgroundPosition;
    const prevBgRepeat = body.style.backgroundRepeat;
    const prevClasses = new Set(Array.from(body.classList));

    body.classList.add("game-active");
    body.classList.remove("common-bg", "common-bg1", "rules-bg");
    body.style.backgroundImage = "none";
    body.style.backgroundSize = "";
    body.style.backgroundPosition = "";
    body.style.backgroundRepeat = "";

    return () => {
      body.classList.remove("game-active");
      body.className = "";
      prevClasses.forEach((c) => body.classList.add(c));
      body.style.backgroundImage = prevBg;
      body.style.backgroundSize = prevBgSize;
      body.style.backgroundPosition = prevBgPos;
      body.style.backgroundRepeat = prevBgRepeat;
    };
  }, []);

  useEffect(() => {
    if (!storedUserData?.sessionId || isDemoBypass) return;
    const uid = getDbUserId(storedUserData);
    const externalUid = getExternalUserId(storedUserData);
    const sid = storedUserData.sessionId;
    const oid = storedUserData.organizationId;
    if (!uid || !sid || !oid || !adminToken) return;

    const syncReportAndMaybeRedirect = async () => {
      const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
      if (!backendBase) return;

      const playerName = resolvePlayerName(storedUserData);
      const allowMultiplePlay = isMultiplaySessionEnabled(storedUserData);

      try {
        const res = await fetch(`${backendBase}/fetchReport`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: uid,
            sessionId: sid,
            organizationId: oid,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!data.success) return;

        if (data.report) {
          const dbTime = data.report.time;
          const normalizedTime = String(dbTime ?? "").trim();
          const isZeroTime =
            !normalizedTime ||
            normalizedTime === "00:00" ||
            normalizedTime === "0" ||
            normalizedTime === "00:00:00" ||
            normalizedTime === "0:00";

          if (!isZeroTime) {
            const rpt = data.report;
            const s1 = rpt.stage1 != null ? Number(rpt.stage1) : null;
            const s2 = rpt.stage2 != null ? Number(rpt.stage2) : null;
            const pts = Number(rpt.points) || 0;
            const gameover = Number(rpt.gameover);
            const stage2Complete = Number.isFinite(gameover) && gameover >= 2;
            const stage1Score = Number.isFinite(s1)
              ? s1
              : Number.isFinite(s2)
                ? Math.max(0, pts - s2)
                : pts;
            if (!allowMultiplePlay) {
              if (stage2Complete) {
                navigate("/thankyou", {
                  replace: true,
                  state: {
                    points: pts,
                    time: dbTime,
                    stageId: rpt.id != null ? rpt.id : undefined,
                    spellScore: Number.isFinite(s1) ? s1 : pts - (Number.isFinite(s2) ? s2 : 0),
                    flightScore: Number.isFinite(s2) ? s2 : 0,
                  },
                });
                return;
              }
              if (wandId) {
                navigate("/stage2/broom", {
                  replace: true,
                  state: {
                    wandId,
                    points: stage1Score,
                    time: dbTime,
                    reportId: rpt.reportId != null && rpt.reportId !== "" ? String(rpt.reportId) : undefined,
                    stageId: rpt.id != null ? String(rpt.id) : undefined,
                  },
                });
                return;
              }
            }

            const stageId = rpt.id;
            if (stageId != null && stageId !== "") {
              stageRowIdRef.current = String(stageId);
            }
            const extRid = rpt.reportId;
            if (extRid != null && extRid !== "") {
              reportIdRef.current = String(extRid);
            }
            return;
          }

          const stageId = data.report.id;
          if (stageId != null && stageId !== "") {
            stageRowIdRef.current = String(stageId);
          }
          const extRid = data.report.reportId;
          if (extRid != null && extRid !== "") {
            reportIdRef.current = String(extRid);
            return;
          }
        } else {
          const startRes = await fetch(`${backendBase}/welcomeStageStart`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${adminToken}`,
            },
            body: JSON.stringify({
              userId: uid,
              sessionId: sid,
              organizationId: oid,
              email: storedUserData.email || "",
              emp_id: storedUserData.employeeId || storedUserData.emp_id || "",
              name: playerName,
              user_type: reportRoleForApi,
            }),
          });
          const startData = await startRes.json().catch(() => ({}));
          if (startRes.ok && startData.success && startData.id != null) {
            stageRowIdRef.current = String(startData.id);
          }
        }

        if (reportIdRef.current) return;

        const role = reportRoleForApi;
        const seedRes = await sendReport({
          sessionId: sid,
          organizationId: oid,
          role,
          token: storedUserData.token || "",
          points: 0,
          time: "00:00",
          gameId: storedUserData.gameId,
          name: playerName,
          userId: externalUid,
        });
        const newRid = extractReportIdFromResponse(seedRes);
        if (newRid != null && newRid !== "") {
          reportIdRef.current = String(newRid);
          if (stageRowIdRef.current) {
            try {
              await fetch(`${backendBase}/welcomeStageLinkReport`, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${adminToken}`,
                },
                body: JSON.stringify({
                  userId: uid,
                  sessionId: sid,
                  organizationId: oid,
                  stageId: stageRowIdRef.current,
                  reportId: String(newRid),
                }),
              });
            } catch (linkErr) {
              console.error("welcomeStageLinkReport:", linkErr);
            }
          }
        }
      } catch (error) {
        console.error("Report sync error:", error);
      }
    };

    syncReportAndMaybeRedirect();
  }, [adminToken, isDemoBypass, navigate]);

  useEffect(() => {
    const compute = () => {
      const body = document.body;
      const mobilePortrait =
        window.innerWidth <= 768 &&
        window.matchMedia &&
        window.matchMedia("(orientation: portrait)").matches;
      const level2 = phaseIndex >= 1;
      const bgUrl = mobilePortrait
        ? level2
          ? game2MobileBg
          : game1MobileBg
        : level2
          ? game2DesktopBg
          : game1DesktopBg;
      body.style.backgroundImage = bgUrl ? `url("${bgUrl}")` : "none";
      body.style.backgroundSize = "100% 100%";
      body.style.backgroundPosition = "center";
      body.style.backgroundRepeat = "no-repeat";
    };

    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("orientationchange", compute);
    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener("resize", compute);
      vv.addEventListener("scroll", compute);
    }

    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("orientationchange", compute);
      if (vv) {
        vv.removeEventListener("resize", compute);
        vv.removeEventListener("scroll", compute);
      }
    };
  }, [phaseIndex]);

  useEffect(() => {
    scoreRef.current = score;
  }, [score]);

  useEffect(() => {
    elapsedTimeRef.current = elapsedTime;
  }, [elapsedTime]);

  useEffect(() => {
    if (gameComplete) return;
    const timer = setInterval(() => setElapsedTime((p) => p + 1), 1000);
    return () => clearInterval(timer);
  }, [gameComplete]);

  const formatTime = () => {
    const min = Math.floor(elapsedTime / 60);
    const sec = elapsedTime % 60;
    return `${String(min).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  };

  const enqueuePopup = useCallback((kind, text) => {
    popupQueueRef.current.push({ kind, text });
    setPopupSlot((prev) => prev ?? popupQueueRef.current.shift() ?? null);
  }, []);

  const showCombo = useCallback(
    (text) => {
      enqueuePopup("combo", text);
    },
    [enqueuePopup]
  );

  const showImmersive = useCallback(
    (text) => {
      enqueuePopup("immersive", text);
    },
    [enqueuePopup]
  );

  const clearPopups = useCallback(() => {
    popupQueueRef.current = [];
    setPopupSlot(null);
  }, []);

  useEffect(() => {
    if (!popupSlot) return;
    const ms = POPUP_DURATION_MS[popupSlot.kind] ?? 2200;
    const tid = window.setTimeout(() => {
      setPopupSlot(popupQueueRef.current.shift() ?? null);
    }, ms);
    return () => window.clearTimeout(tid);
  }, [popupSlot]);

  const beginPhasePlay = useCallback((idx) => {
    window.clearTimeout(introTimerRef.current);
    introTimerRef.current = 0;
    phaseEndingRef.current = false;
    clearPopups();
    phaseStartRef.current = performance.now();
    nextSpawnAtRef.current = performance.now() + 400;
    targetsRef.current = [];
    projectilesRef.current = [];
    setTargetsSnap([]);
    setBoltsSnap([]);
    setWandAnchorRatio(0.5);
    wandRatioPendingRef.current = 0.5;
    setPhaseUi("play");
    phaseUiRef.current = "play";
    if (idx === 1 && Math.random() < 0.45) {
      showImmersive(IMMERSIVE.hogwartsEcho);
    }
  }, [showImmersive, clearPopups]);

  const advanceAfterPhaseEnd = useCallback(() => {
    clearPopups();
    const idx = phaseIndexRef.current;
    if (idx >= PHASES.length - 1) {
      setPhaseUi("postGame");
      phaseUiRef.current = "postGame";
      setPhaseUiText(PHASES[PHASES.length - 1].end);
      setSpellLevelDone(true);
      spellLevelDoneRef.current = true;
      return;
    }
    const next = idx + 1;
    setPhaseIndex(next);
    phaseIndexRef.current = next;
    setPhaseUi("intro");
    phaseUiRef.current = "intro";
    setPhaseUiText(PHASES[next].intro);
  }, [clearPopups]);

  useEffect(() => {
    setPhaseUiText(PHASES[phaseIndex].intro);
  }, [phaseIndex]);

  useEffect(() => {
    if (phaseUi !== "intro") return;
    window.clearTimeout(introTimerRef.current);
    introTimerRef.current = 0;
    return () => {
      window.clearTimeout(introTimerRef.current);
      introTimerRef.current = 0;
    };
  }, [phaseUi, phaseIndex]);

  useEffect(() => {
    if (phaseUi !== "phaseEnd") return;
    const t = window.setTimeout(() => advanceAfterPhaseEnd(), 2600);
    return () => window.clearTimeout(t);
  }, [phaseUi, advanceAfterPhaseEnd]);

  const spawnTarget = useCallback(
    (now) => {
      const el = stageRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const idx = phaseIndexRef.current;
      const cfg = PHASES[idx];
      const friendly = Math.random() < cfg.friendlyChance;
      const pool = friendly ? FRIENDLY_TARGETS : DARK_TARGETS;
      const def = pick(pool);
      const w = Math.min(86, rect.width * 0.14);
      const h = w * 1.05;
      const marginTop = rect.height * 0.06;
      const wandBand = rect.height * 0.42;
      const maxY = rect.height - wandBand - h;
      const aimYMin = marginTop + h * 0.35;
      const aimYMax = Math.max(aimYMin + 1, maxY - h * 0.2);
      const anchorId = pick(SPELL_SPAWN_ANCHOR_IDS);
      const anchorEl = el.querySelector(`#${anchorId}`);
      let cx = rect.width * 0.5;
      let cy = rect.height * 0.28;
      if (anchorEl) {
        const ar = anchorEl.getBoundingClientRect();
        cx = ar.left + ar.width / 2 - rect.left;
        cy = ar.top + ar.height / 2 - rect.top;
      }
      const jitterX = rect.width * 0.022 * (Math.random() - 0.5);
      const jitterY = rect.height * 0.016 * (Math.random() - 0.5);
      let x = cx - w / 2 + jitterX;
      let y = cy - h / 2 + jitterY;
      x = Math.max(-w * 0.35, Math.min(rect.width - w * 0.65, x));
      y = Math.max(marginTop * 0.5, Math.min(maxY - h * 0.15, y));

      const aimX = rect.width * (0.18 + Math.random() * 0.64);
      const aimY = aimYMin + Math.random() * (aimYMax - aimYMin);
      const tcx = x + w / 2;
      const tcy = y + h / 2;
      const dx = aimX - tcx;
      const dy = aimY - tcy;
      const dist = Math.hypot(dx, dy);
      const base = cfg.speedMin + Math.random() * (cfg.speedMax - cfg.speedMin);
      let vx;
      let vy;
      if (dist < 24) {
        vx = (Math.random() - 0.5) * cfg.vyAmp;
        vy = base * (0.85 + Math.random() * 0.25);
      } else {
        vx = (dx / dist) * base;
        vy = (dy / dist) * base;
        const tang = cfg.vyAmp * (Math.random() - 0.5) * 0.22;
        const k = 0.02 / dist;
        vx += -dy * tang * k;
        vy += dx * tang * k;
      }
      const id = idRef.current++;
      targetsRef.current.push({
        id, x, y, vx, vy, w, h,
        src: def.src,
        kind: friendly ? "friendly" : "dark",
        flavor: def.flavor,
        hit: false,
        born: now,
      });

      if (friendly && def.flavor === "hedwig" && Math.random() < 0.55) {
        showImmersive(IMMERSIVE.hedwig);
      } else if (friendly && def.flavor === "snitch" && Math.random() < 0.5) {
        showImmersive(IMMERSIVE.snitch);
      }
    },
    [showImmersive]
  );

  // ── UPDATED: tryCastSpell now stores wand tip as bolt origin ──────────────
  const tryCastSpell = useCallback(
    (clientX, clientY) => {
      if (phaseUiRef.current !== "play" || spellLevelDoneRef.current) return;
      const el = stageRef.current;
      const wandEl = wandRef.current;
      if (!el || !wandEl) return;
      const s = el.getBoundingClientRect();
      const wrect = wandEl.getBoundingClientRect();

      // Wand tip: top-center of wand hand image (adjust ratio as needed per art)
      const tipX = wrect.left + wrect.width * 0.38 - s.left;
      const tipY = wrect.top + wrect.height * 0.15 - s.top;

      const relX = clientX - s.left;
      const relY = clientY - s.top;
      const dx = relX - tipX;
      const dy = relY - tipY;
      const len = Math.hypot(dx, dy) || 1;
      const speed = 640;
      const id = idRef.current++;

      projectilesRef.current.push({
        id,
        // store wand-tip origin for canvas bolt rendering
        fromX: tipX,
        fromY: tipY,
        // current head position (travels toward target)
        x: tipX,
        y: tipY,
        vx: (dx / len) * speed,
        vy: (dy / len) * speed,
        angle: Math.atan2(dy, dx),
        hitR: 20,
      });
    },
    []
  );

  const scheduleWandRatio = useCallback((ratio) => {
    wandRatioPendingRef.current = ratio;
    if (wandMoveRafRef.current) return;
    wandMoveRafRef.current = requestAnimationFrame(() => {
      wandMoveRafRef.current = 0;
      setWandAnchorRatio(wandRatioPendingRef.current);
    });
  }, []);

  const onPointerMove = useCallback(
    (e) => {
      const el = stageRef.current;
      if (!el || phaseUiRef.current !== "play" || spellLevelDoneRef.current) return;
      const r = el.getBoundingClientRect();
      if (r.width < 8) return;
      const mx = (e.clientX - r.left) / r.width;
      const clamped = Math.max(0.1, Math.min(0.9, mx));
      scheduleWandRatio(clamped);
    },
    [scheduleWandRatio]
  );

  const onPointerDown = (e) => {
    if (phaseUi === "intro") {
      beginPhasePlay(phaseIndex);
      return;
    }
    if (phaseUi === "phaseEnd" || phaseUi === "postGame") return;
    if (e.target.closest(".welcome-static-actions")) return;
    tryCastSpell(e.clientX, e.clientY);
  };

  const createFragments = (target, collisionX, collisionY) => {
    const frags = [];
    const fragmentCount = 8;
    for (let i = 0; i < fragmentCount; i++) {
      const angle = (i / fragmentCount) * Math.PI * 2;
      const velocityX = Math.cos(angle) * (50 + Math.random() * 100);
      const velocityY = Math.sin(angle) * (50 + Math.random() * 100);
      const rotation = Math.random() * 720 - 360;
      frags.push({
        id: Date.now() + Math.random() + i,
        x: collisionX, y: collisionY,
        vx: velocityX, vy: velocityY,
        rotation, size: 15 + Math.random() * 20,
        src: target.src, kind: target.kind
      });
    }
    return frags;
  };

  useEffect(() => {
    const loop = (ts) => {
      const last = lastTsRef.current || ts;
      const dt = Math.min(0.05, (ts - last) / 1000);
      lastTsRef.current = ts;

      if (phaseUiRef.current === "play" && !spellLevelDoneRef.current) {
        const rect = stageRef.current?.getBoundingClientRect();
        const idx = phaseIndexRef.current;
        const cfg = PHASES[idx];
        const elapsed = ts - phaseStartRef.current;

        if (elapsed >= cfg.durationMs) {
          if (!phaseEndingRef.current) {
            phaseEndingRef.current = true;
            clearPopups();
            targetsRef.current = [];
            projectilesRef.current = [];
            setTargetsSnap([]);
            setBoltsSnap([]);
            setPhaseUi("phaseEnd");
            phaseUiRef.current = "phaseEnd";
            setPhaseUiText(cfg.end);
          }
        } else {
          if (ts >= nextSpawnAtRef.current) {
            const bMin = Math.max(1, cfg.spawnBurstMin ?? 1);
            const bMax = Math.max(bMin, cfg.spawnBurstMax ?? bMin);
            const burst = bMin + Math.floor(Math.random() * (bMax - bMin + 1));
            for (let i = 0; i < burst; i++) spawnTarget(ts);
            const gap = cfg.spawnMin + Math.random() * (cfg.spawnMax - cfg.spawnMin);
            nextSpawnAtRef.current = ts + gap;
          }

          const tw = rect?.width ?? 400;
          const th = rect?.height ?? 600;

          for (const t of targetsRef.current) {
            if (t.hit) continue;
            t.x += t.vx * dt;
            t.y += t.vy * dt + Math.sin((ts - t.born) / 420) * 0.35;
            if (t.x < -120 || t.x > tw + 120 || t.y < -150 || t.y > th + 120) {
              t.hit = true;
            }
          }
          targetsRef.current = targetsRef.current.filter((t) => !t.hit);

          for (const p of projectilesRef.current) {
            p.x += p.vx * dt;
            p.y += p.vy * dt;
          }

          // Fragment physics
          for (const frag of fragments) {
            frag.x += frag.vx * dt;
            frag.y += frag.vy * dt;
            frag.vy += 300 * dt;
            frag.rotation += frag.vx * dt * 5;
          }

          const pr = projectilesRef.current;
          for (let pi = pr.length - 1; pi >= 0; pi--) {
            const p = pr[pi];
            let absorbed = false;
            for (const t of targetsRef.current) {
              if (t.hit) continue;
              const cx = t.x + t.w / 2;
              const cy = t.y + t.h / 2;
              const prHit = p.hitR ?? 12;
              const rad = Math.max(t.w, t.h) * 0.45 + prHit;
              if (distSq(p.x, p.y, cx, cy) < rad * rad) {
                absorbed = true;

                if (!t.hit) {
                  const collisionX = p.x;
                  const collisionY = p.y;

                  setCrashingTargets(prev => [...prev, {
                    id: t.id, x: t.x, y: t.y, w: t.w, h: t.h, kind: t.kind, src: t.src
                  }]);

                  const newFragments = createFragments(t, collisionX, collisionY);
                  setFragments(prev => [...prev, ...newFragments]);

                  setGlassBreaks(prev => [...prev, {
                    id: Date.now() + Math.random(), x: cx, y: cy
                  }]);

                  setTimeout(() => {
                    setCrashingTargets(prev => prev.filter(c => c.id !== t.id));
                  }, 400);
                  setTimeout(() => {
                    setFragments(prev => prev.filter(f => !newFragments.some(nf => nf.id === f.id)));
                  }, 600);
                  setTimeout(() => {
                    setGlassBreaks(prev => prev.slice(1));
                  }, 300);
                }

                t.hit = true;

                if (t.kind === "dark") {
                  scoreRef.current += DARK_HIT;
                  setScore((s) => s + DARK_HIT);
                  totalImagesCountRef.current += 1;
                  streakRef.current += 1;
                  const st = streakRef.current;
                  setStreak(st);
                  setHitFlash(true);
                  window.setTimeout(() => setHitFlash(false), 220);

                  if (isDemoBypass) {
                    if (!demoTargetsHitRef.current) demoTargetsHitRef.current = 0;
                    demoTargetsHitRef.current++;
                    if (demoTargetsHitRef.current >= 2 && !demoRedirectTriggeredRef.current) {
                      demoRedirectTriggeredRef.current = true;
                      if (rafRef.current) {
                        cancelAnimationFrame(rafRef.current);
                        rafRef.current = 0;
                      }
                      setTimeout(() => {
                        Swal.fire({
                          icon: "info",
                          text: "Thank you for playing. Subscribe to any PLAN to play with your peers.",
                          confirmButtonText: "OK",
                          allowOutsideClick: false,
                          allowEscapeKey: false,
                        }).then(() => {
                          window.location.assign("https://extramileplay.com/plans");
                        });
                      }, 400);
                      return;
                    }
                  }

                  if (st === 3) showCombo("Nice streak ×3!");
                  else if (st === 5) {
                    showCombo("Brilliant — ×5!");
                    showImmersive(IMMERSIVE.patronus);
                  } else if (st === 8) showCombo("Legendary control ×8!");
                } else {
                  wrongCountRef.current += 1;
                  scoreRef.current = Math.max(0, scoreRef.current + FRIENDLY_HIT);
                  setScore((s) => Math.max(0, s + FRIENDLY_HIT));
                  streakRef.current = 0;
                  setStreak(0);
                  enqueuePopup("penalty", "Wrong target — friendly! −5");
                  setPenaltyFlash(true);
                  window.setTimeout(() => setPenaltyFlash(false), 400);
                }
                break;
              }
            }
            if (absorbed || p.x < -40 || p.y < -40 || p.x > tw + 40 || p.y > th + 40) {
              pr.splice(pi, 1);
            }
          }

          setTargetsSnap(targetsRef.current.slice());
          // Build bolt data: from wand tip to current projectile head
          setBoltsSnap(projectilesRef.current.map(p => ({
            id: p.id,
            fromX: p.fromX ?? p.x,
            fromY: p.fromY ?? p.y,
            toX: p.x,
            toY: p.y,
          })));
        }
      }

      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [spawnTarget, showCombo, showImmersive, enqueuePopup, clearPopups]);

  const completeGame = async () => {
    if (finishOnceRef.current) return;
    finishOnceRef.current = true;
    setGameComplete(true);
    clearPopups();

    const currentPoints = scoreRef.current;
    const et = elapsedTimeRef.current;
    const finishedTime = `${String(Math.floor(et / 60)).padStart(2, "0")}:${String(et % 60).padStart(2, "0")}`;
    const currentQuesPayload = JSON.stringify(readHogwartsSelectionArray(storedUserData));

    try {
      if (!isDemoBypass) {
        const uid = getDbUserId(storedUserData);
    const externalUid = getExternalUserId(storedUserData);
        const sid = storedUserData.sessionId;
        const oid = storedUserData.organizationId;
        const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

        if (backendBase && uid && sid && oid && adminToken) {
          const rowId = await ensureWelcomeStageRowId({
            backendBase,
            adminToken,
            storedUserData,
            playerName: resolvePlayerName(storedUserData),
            user_type: reportRoleForApi,
          });
          if (rowId) stageRowIdRef.current = rowId;
          if (!stageRowIdRef.current) {
            console.warn("welcomeStageComplete skipped: no stages row id");
          }
        }
        if (backendBase && stageRowIdRef.current && uid && sid && oid && adminToken) {
          try {
            const stageRes = await fetch(`${backendBase}/welcomeStageComplete`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${adminToken}`,
              },
              body: JSON.stringify({
                userId: uid, sessionId: sid, organizationId: oid,
                stageId: stageRowIdRef.current,
                time: finishedTime,
                total_score: Number(currentPoints) || 0,
                stage1: Number(currentPoints) || 0,
                wrongcount: wrongCountRef.current,
                currentQues: currentQuesPayload,
              }),
            });
            if (!stageRes.ok) {
              const errTxt = await stageRes.text().catch(() => "");
              console.error("welcomeStageComplete HTTP", stageRes.status, errTxt);
            }
          } catch (stageErr) {
            console.error("welcomeStageComplete:", stageErr);
          }
        }

        const role = reportRoleForApi;
        await sendReport({
          ...(reportIdRef.current ? { reportId: reportIdRef.current } : {}),
          sessionId: sid, organizationId: oid, role,
          token: storedUserData.token || "",
          points: currentPoints, time: finishedTime,
          gameId: storedUserData.gameId,
          name: resolvePlayerName(storedUserData),
          userId: externalUid,
        });
      }
    } catch (err) {
      console.error("Score update failed:", err);
    }

    navigate("/stage2/broom", {
      replace: true,
      state: {
        houseId, characterId, characterName, wandId, wandName,
        points: currentPoints, time: finishedTime,
        reportId: reportIdRef.current || undefined,
        stageId: stageRowIdRef.current || undefined,
      },
    });
  };

  useLayoutEffect(() => {
    completeGameRef.current = completeGame;
  });

  useEffect(() => {
    if (phaseUi !== "postGame" || gameComplete) return;
    const delayMs = 2800;
    const id = window.setTimeout(() => {
      completeGameRef.current?.();
    }, delayMs);
    return () => window.clearTimeout(id);
  }, [phaseUi, gameComplete]);

  if (!wandId) {
    return <Navigate to="/wand" replace />;
  }

  const screenFlashOverlay =
    (penaltyFlash || hitFlash) &&
    createPortal(
      <div
        className={`spellcasting-screen-flash${penaltyFlash ? " spellcasting-screen-flash--penalty" : ""}${
          hitFlash ? " spellcasting-screen-flash--hit" : ""
        }`}
        aria-hidden
      />,
      document.body
    );

  return (
    <>
      {screenFlashOverlay}
      <div className="game-container welcome-static-page spellcasting-root">
      {rulesModalOpen ? (
        <div
          className="welcome-rules-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="welcome-rules-modal-title"
        >
          <div className="spell-message-panel welcome-rules-modal-panel">
            <h2 id="welcome-rules-modal-title" className="welcome-rules-modal-title">
            Spellcasting Trial
            </h2>
            <ul className="welcome-rules-modal-list">
              {TRIAL_RULES_ITEMS.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
            <button
              type="button"
              className="welcome-rules-modal-close"
              onClick={() => setRulesModalOpen(false)}
            >
              Close
            </button>
          </div>
        </div>
      ) : null}

      <div style={{ position: "relative", zIndex: 15 }}>
        <header
          className="game-hud-bar game-hud-bar--welcome game-hud-bar--welcome-spellcasting"
          aria-label="Game status"
        >
          <div className="game-hud-time" style={{ visibility: 'hidden' }}>
            <img src={timeIcon} alt="" className="game-hud-icon" width={28} height={28} />
            <span id="timer" className="game-hud-time-value">
              {formatTime()}
            </span>
          </div>
          <div className="game-hud-points">
            <button
              type="button"
              className="welcome-rules-help"
              onClick={() => setRulesModalOpen(true)}
              aria-label="Show trial rules"
            >
              ?
            </button>
            <img src={pointIcon} alt="" className="game-hud-icon" width={28} height={28} />
            <div className="game-hud-points-inner">
              <span id="score" className="game-hud-points-value">
                {score}
              </span>
            </div>
          </div>
        </header>

        <div
          ref={stageRef}
          className="spell-stage bin-toss-game-touch"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          role="application"
          aria-label="Spellcasting trial — tap to cast toward targets"
        >
          <div className="spell-spawn-markers" aria-hidden>
            <div id="spell-spawn-1" className="spell-spawn-anchor" />
            <div id="spell-spawn-2" className="spell-spawn-anchor" />
            <div id="spell-spawn-3" className="spell-spawn-anchor" />
            <div id="spell-spawn-4" className="spell-spawn-anchor" />
            <div id="spell-spawn-5" className="spell-spawn-anchor" />
          </div>

          {/* Active targets */}
          {targetsSnap.map((t) => {
            const isCrashing = crashingTargets.some(ct => ct.id === t.id);
            return (
              <div
                key={t.id}
                className={`spell-target spell-target--${t.kind}${isCrashing ? ' crashing' : ''}`}
                style={{ left: t.x, top: t.y, width: t.w, height: t.h }}
              >
                <img src={t.src} alt="" draggable={false} />
              </div>
            );
          })}

          {/* Crashing targets */}
          {crashingTargets.map((ct) => (
            <div
              key={`crash-${ct.id}`}
              className={`spell-target spell-target--${ct.kind} crashing`}
              style={{ left: ct.x, top: ct.y, width: ct.w, height: ct.h, position: 'absolute', zIndex: 20 }}
            >
              <img src={ct.src} alt="" draggable={false} />
            </div>
          ))}

          {/* Fragments */}
          {fragments.map((frag) => (
            <div
              key={`frag-${frag.id}`}
              className="spell-shatter-fragment"
              style={{
                left: frag.x - frag.size / 2, top: frag.y - frag.size / 2,
                width: frag.size, height: frag.size,
                backgroundImage: `url(${frag.src})`,
                backgroundSize: 'cover', backgroundPosition: 'center',
                transform: `rotate(${frag.rotation}deg)`,
                position: 'absolute',
                '--tx': `${(Math.random() - 0.5) * 200}px`,
                '--ty': `${(Math.random() - 0.5) * 200 - 100}px`,
                '--rot': `${frag.rotation + 360}deg`,
              }}
            />
          ))}

          {/* Glass break */}
          {glassBreaks.map((gb) => (
            <div
              key={`glass-${gb.id}`}
              className="spell-glass-break"
              style={{ left: gb.x - 15, top: gb.y - 15, width: 30, height: 30, position: 'absolute' }}
            />
          ))}

          {/* ── NEW: Canvas-based dynamic lightning bolts ── */}
          {boltsSnap.length > 0 && (
            <BoltCanvas
              key={boltsSnap.map(b => b.id).join(',')}
              bolt={boltsSnap[boltsSnap.length - 1]}
              stageW={stageSize.w}
              stageH={stageSize.h}
            />
          )}

          {/* Wand */}
          <div
            className="spell-wand-wrap"
            style={{ left: `${wandAnchorRatio * 100}%` }}
            aria-hidden
          >
            <img
              ref={wandRef}
              src={handSrc}
              alt=""
              className="spell-wand-hand"
              draggable={false}
            />
          </div>

          {phaseUi !== "play" && phaseUi !== "postGame" ? (
            <div className="spell-phase-overlay">
              <div className="spell-message-panel">
                <p className="spell-phase-overlay__text">{phaseUiText}</p>
                {phaseUi === "intro" ? (
                  <p className="spell-phase-overlay__hint">Tap to begin this phase</p>
                ) : null}
              </div>
            </div>
          ) : null}

          {phaseUi === "postGame" ? (
            <div className="spell-phase-overlay spell-phase-overlay--finale">
              <div className="spell-message-panel">
                <p className="spell-phase-overlay__text">{phaseUiText}</p>
              </div>
            </div>
          ) : null}

          {popupSlot ? (
            <div
              className={`spell-toast spell-toast--${popupSlot.kind}`}
              role={popupSlot.kind === "penalty" ? "alert" : "status"}
            >
              {popupSlot.text}
            </div>
          ) : null}
        </div>

        <div className="welcome-static-actions">
          {/* Button intentionally hidden */}
        </div>
      </div>
    </div>
    </>
  );
}

export default Welcome;
